import { useState, useEffect, useMemo } from 'react'
import { Check, X, Loader2, AlertCircle, Lightbulb, Lock, AlertTriangle } from 'lucide-react'
import Input from '@/components/common/Input'
import { validateUsername, formatUsername, generateUsernameSuggestions } from '@/utils/username'
import { useUsernameCheck } from '@/hooks/useUsernameCheck'
import { useUpdateUsername } from '@/hooks/useUpdateUsername'
import { getFullProfileUrl } from '@/utils/profileUrl'
import { useDebounce } from '@/hooks/useDebounce'
import { useTranslation } from 'react-i18next'

interface UsernameSelectorProps {
  currentUsername?: string
  firstName?: string
  lastName?: string
}

export default function UsernameSelector({
  currentUsername,
  firstName,
  lastName,
}: UsernameSelectorProps) {
  const { t } = useTranslation('profile')
  const [username, setUsername] = useState(currentUsername || '')
  const [hasChanged, setHasChanged] = useState(false)
  const debouncedUsername = useDebounce(username, 500)

  const updateUsername = useUpdateUsername()

  // Username is locked after first save
  const isLocked = !!currentUsername

  // Generate suggestions from name
  const nameSuggestions = useMemo(() => {
    if (firstName && lastName) {
      return generateUsernameSuggestions(firstName, lastName)
    }
    return []
  }, [firstName, lastName])

  // Check username availability (only if changed and different from current)
  const shouldCheck =
    !isLocked &&
    hasChanged &&
    debouncedUsername !== currentUsername &&
    debouncedUsername.length >= 3

  const { data: availability, isLoading: isChecking } = useUsernameCheck(
    debouncedUsername,
    shouldCheck
  )

  // Validate on client side
  const validation = validateUsername(username)

  useEffect(() => {
    if (currentUsername) {
      setUsername(currentUsername)
      setHasChanged(false)
    }
  }, [currentUsername])

  const handleUsernameChange = (value: string) => {
    if (isLocked) return // Don't allow changes if locked
    const formatted = formatUsername(value)
    setUsername(formatted)
    setHasChanged(true)
  }

  const handleSave = async () => {
    if (!validation.valid || username === currentUsername || isLocked) return

    await updateUsername.mutateAsync(username)
    setHasChanged(false)
  }

  const isAvailable = availability?.available ?? true
  const canSave =
    !isLocked &&
    validation.valid &&
    hasChanged &&
    username !== currentUsername &&
    isAvailable &&
    !isChecking

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
          {t('sharing.usernameSelector.title')}
          {isLocked && <Lock className="w-4 h-4 text-gray-500" />}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {isLocked
            ? t('sharing.usernameSelector.descriptionLocked')
            : t('sharing.usernameSelector.descriptionUnlocked')}
        </p>
      </div>

      {/* WARNING: Before setting username */}
      {!isLocked && (
        <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-yellow-900 mb-1">
                {t('sharing.usernameSelector.warningTitle')}
              </p>
              <p className="text-sm text-yellow-800">
                {t('sharing.usernameSelector.warningMessage')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* INFO: After username is set */}
      {isLocked && (
        <div className="p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">
                {t('sharing.usernameSelector.lockedTitle')}
              </p>
              <p className="text-sm text-blue-800">
                {t('sharing.usernameSelector.lockedMessage')}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="relative">
          <Input
            value={username}
            onChange={(e) => handleUsernameChange(e.target.value)}
            placeholder="john-doe"
            maxLength={30}
            disabled={isLocked}
            className={isLocked ? 'bg-gray-100 cursor-not-allowed' : ''}
          />

          {/* Lock icon for locked state */}
          {isLocked && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Lock className="w-5 h-5 text-gray-400" />
            </div>
          )}

          {/* Status indicator (only when not locked) */}
          {!isLocked && username && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isChecking ? (
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              ) : !validation.valid ? (
                <X className="w-5 h-5 text-red-500" />
              ) : isAvailable ? (
                <Check className="w-5 h-5 text-green-500" />
              ) : (
                <X className="w-5 h-5 text-red-500" />
              )}
            </div>
          )}
        </div>

        {/* Validation/availability messages (only when not locked) */}
        {!isLocked && username && (
          <div className="space-y-2">
            {!validation.valid && (
              <p className="text-sm text-red-600 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {validation.error}
              </p>
            )}

            {validation.valid && !isChecking && !isAvailable && (
              <div className="space-y-2">
                <p className="text-sm text-red-600 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {t('sharing.usernameSelector.alreadyTaken')}
                </p>
                {availability?.suggestions && availability.suggestions.length > 0 && (
                  <div className="text-sm">
                    <p className="text-gray-600 mb-1">{t('sharing.usernameSelector.suggestions')}</p>
                    <div className="flex flex-wrap gap-2">
                      {availability.suggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          onClick={() => setUsername(suggestion)}
                          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {validation.valid && isAvailable && username !== currentUsername && (
              <p className="text-sm text-green-600 flex items-center gap-2">
                <Check className="w-4 h-4" />
                {t('sharing.usernameSelector.available')}
              </p>
            )}
          </div>
        )}

        {/* Show suggestions when no username is set */}
        {!currentUsername && !username && nameSuggestions.length > 0 && (
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2 mb-2">
              <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5" />
              <p className="text-sm font-medium text-blue-900">
                {t('sharing.usernameSelector.trySuggestions')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {nameSuggestions.slice(0, 5).map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => {
                    setUsername(suggestion)
                    setHasChanged(true)
                  }}
                  className="px-3 py-1.5 bg-white hover:bg-blue-100 rounded-lg text-blue-700 font-medium text-sm border border-blue-200 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Character count */}
        {!isLocked && (
          <p className="text-xs text-gray-500">
            {username.length}/30 {t('sharing.usernameSelector.characters')}
          </p>
        )}

        {/* Preview URL */}
        {username && validation.valid && (
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-1">
              {t('sharing.usernameSelector.profileUrl')}
            </p>
            <p className="text-sm text-blue-600 font-mono break-all">
              {getFullProfileUrl(username)}
            </p>
          </div>
        )}

        {/* Save button (only when not locked) */}
        {!isLocked && hasChanged && username !== currentUsername && (
          <button
            onClick={handleSave}
            disabled={!canSave || updateUsername.isPending}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateUsername.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                {t('sharing.usernameSelector.saving')}
              </>
            ) : (
              t('sharing.usernameSelector.saveButton')
            )}
          </button>
        )}
      </div>
    </div>
  )
}
